/**
 * JWTトークンの有効期限チェックヘルパー
 */

/**
 * JWTトークンをデコードして有効期限を取得
 */
export function decodeToken(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
}

/**
 * トークンの有効期限が切れているかチェック
 * @param token JWTトークン
 * @param bufferSeconds 期限切れ前の余裕時間（デフォルト: 30秒）
 * @returns true: 期限切れまたは期限切れ間近, false: まだ有効
 */
export function isTokenExpired(
  token: string | null,
  bufferSeconds = 30,
): boolean {
  if (!token) {
    return true;
  }

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const expirationTime = decoded.exp;

  // バッファ時間を考慮して期限切れ判定
  return currentTime >= expirationTime - bufferSeconds;
}

/**
 * トークンの残り有効時間を取得（秒単位）
 * @param token JWTトークン
 * @returns 残り有効時間（秒）、無効な場合は-1
 */
export function getTokenRemainingTime(token: string | null): number {
  if (!token) {
    return -1;
  }

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return -1;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const remainingTime = decoded.exp - currentTime;

  return Math.max(0, remainingTime);
}

/**
 * Auth0のローカルストレージからトークン情報を取得
 * @param clientId Auth0のクライアントID
 * @returns トークン情報またはnull
 */
export function getStoredTokenInfo(
  clientId: string,
): {
    accessToken: string | null;
    expiresAt: number | null;
  } {
  // Auth0 SPA SDKのローカルストレージキー形式
  const authKey = `@@auth0spajs@@::${clientId}`;
  const keys = Object.keys(localStorage).filter((key) => key.startsWith(authKey));

  for (const key of keys) {
    const data = localStorage.getItem(key);
    if (data) {
      try {
        const parsed = JSON.parse(data);
        if (parsed.body?.access_token) {
          return {
            accessToken: parsed.body.access_token,
            expiresAt: parsed.body.expires_in
              ? Math.floor(Date.now() / 1000) + parsed.body.expires_in
              : null,
          };
        }
      } catch (error) {
        // パースエラーなどは無視
      }
    }
  }

  return { accessToken: null, expiresAt: null };
}
